import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Mail, RefreshCw } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";

// Business-facing buckets (§G): the backend outbox state machine is untouched —
// technical states are grouped only for presentation, and remain visible in row detail.
const BUCKETS = [
  { key: null, label: "All", statuses: [] },
  { key: "pending,sending", label: "Pending", statuses: ["pending", "sending"] },
  { key: "sent", label: "Accepted", statuses: ["sent"] },
  { key: "failed,dead", label: "Failed", statuses: ["failed", "dead"] },
  { key: "captured", label: "Captured (test — not sent)", statuses: ["captured"] },
];
const STATUS_BADGE = {
  pending: "badge-reserved",
  sending: "badge-reserved",
  sent: "badge-available",
  failed: "badge-neutral",
  dead: "badge-neutral",
};
const BUCKET_LABEL = { pending: "Pending", sending: "Pending", sent: "Accepted by provider", failed: "Failed", dead: "Failed", captured: "Automated test email — captured and not sent" };
// §28.1: provider-confirmed lifecycle states — shown only when an authoritative
// provider event exists; "Accepted by provider" is never presented as delivered.
const DELIVERY_LABEL = { accepted: "Accepted", delivered: "Delivered", deferred: "Deferred", bounced: "Bounced", failed: "Failed", suppressed: "Suppressed" };
const DELIVERY_BADGE = { accepted: "badge-neutral", delivered: "badge-available", deferred: "badge-reserved", bounced: "badge-sold", failed: "badge-sold", suppressed: "badge-sold" };
const bucketCount = (counts, b) => (b.key === null
  ? Object.values(counts).reduce((n, v) => n + v, 0)
  : b.statuses.reduce((n, s) => n + (counts[s] || 0), 0));

function Detail({ label, value, mono = false, testid }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className={`mt-1 break-all text-sm text-charcoal ${mono ? "font-mono text-xs" : ""}`} data-testid={testid}>
        {value ?? "—"}
      </p>
    </div>
  );
}

export default function AdminEmailLog() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [status, setStatus] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);
  const debounce = useRef(null);

  const load = useCallback((cursor = null, append = false, s = status, to = recipient) => {
    const params = new URLSearchParams();
    if (s) params.set("status", s);
    if (to.trim()) params.set("to", to.trim());
    if (cursor) params.set("cursor", cursor);
    return api
      .get(`/admin/email-log?${params.toString()}`)
      .then((res) => {
        setError(false);
        setRows((prev) => (append && prev ? [...prev, ...res.data.rows] : res.data.rows));
        setNextCursor(res.data.next_cursor);
        setStatusCounts(res.data.status_counts || {});
      })
      .catch((e) => {
        setError(true);
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      });
  }, [status, recipient]);

  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onRecipientChange = (v) => {
    setRecipient(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setRows(null);
      load(null, false, status, v);
    }, 350);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    await load(nextCursor, true);
    setLoadingMore(false);
  };

  return (
    <div data-testid="email-log-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Operations</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Email Log</h1>
          <p className="mt-2 text-sm text-muted-foreground">Transactional email outbox — read-only. Delivery retries happen automatically; Failed may include messages whose automatic retries were exhausted.</p>
        </div>
        <Button variant="outline" onClick={() => load()} data-testid="email-log-refresh" className="border-limestone text-charcoal hover:bg-sand">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="email-log-filters">
        {BUCKETS.map((b) => (
          <span key={b.label} className="inline-flex items-center gap-1">
            <button
              onClick={() => setStatus(b.key)}
              data-testid={b.key === null ? "email-log-filter-all" : `email-log-filter-${b.label.toLowerCase()}`}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${status === b.key ? "border-terracotta bg-terracotta text-white" : "border-limestone bg-white text-warmgrey hover:text-charcoal"}`}
            >
              {b.label}{statusCounts && Object.keys(statusCounts).length > 0 ? ` (${bucketCount(statusCounts, b)})` : ""}
            </button>
            {b.label === "Failed" && (
              <HelpTip testid="help-email-failed" text="Failed may include messages for which automatic retry attempts have been exhausted. Open a row to see the technical state (failed vs dead), attempts and the provider error." />
            )}
          </span>
        ))}
        <Input
          value={recipient}
          onChange={(e) => onRecipientChange(e.target.value)}
          placeholder="Filter by recipient…"
          data-testid="email-log-search"
          className="ml-auto h-9 w-64 bg-white focus-visible:ring-terracotta"
        />
      </div>

      {!rows ? (
        error ? (
          <Card className="mt-8 border-border bg-white" data-testid="email-log-error">
            <CardContent className="p-12 text-center text-sm text-muted-foreground">Email log unavailable — please retry.</CardContent>
          </Card>
        ) : (
          <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" data-testid="email-log-loading" />
        )
      ) : rows.length === 0 ? (
        <Card className="mt-8 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <Mail className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="email-log-empty">No email log entries match.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="mt-6 overflow-hidden border-border bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="email-log-table">
                <thead>
                  <tr className="border-b border-border text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Recipient</th>
                    <th className="px-5 py-3">Template</th>
                    <th className="px-5 py-3">Attempts</th>
                    <th className="px-5 py-3">Last error</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3">Sent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => setDetail(r)}
                      data-testid={`email-log-row-${r.id}`}
                      className="cursor-pointer transition-colors duration-150 hover:bg-sand/50"
                    >
                      <td className="px-5 py-3">
                        <span className={`badge-status ${STATUS_BADGE[r.status] || "badge-neutral"}`} data-testid={`email-log-status-${r.id}`}>{BUCKET_LABEL[r.status] || r.status}</span>
                        {r.delivery_state && (
                          <span className={`ml-1.5 badge-status ${DELIVERY_BADGE[r.delivery_state] || "badge-neutral"}`} data-testid={`email-log-delivery-${r.id}`}>
                            {DELIVERY_LABEL[r.delivery_state] || r.delivery_state}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-charcoal">{r.to}</td>
                      <td className="px-5 py-3 font-mono text-xs text-warmgrey">{r.template}</td>
                      <td className="px-5 py-3 text-charcoal">{r.attempts}</td>
                      <td className="max-w-48 truncate px-5 py-3 text-xs text-muted-foreground">{r.last_error || "—"}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-muted-foreground">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {nextCursor && (
            <div className="mt-4 text-center">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="email-log-load-more" className="border-limestone text-charcoal hover:bg-sand">
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="border-border bg-white sm:max-w-lg" data-testid="email-log-detail">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Email — {BUCKET_LABEL[detail?.status] || detail?.status}</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              Outbox row detail. Stored content is data only — rendered HTML is never shown or re-sent from here.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Detail label="Technical status" value={detail.status} testid="email-log-detail-status" />
              <Detail label="Recipient" value={detail.to} testid="email-log-detail-to" />
              <Detail label="Template" value={detail.template} mono testid="email-log-detail-template" />
              <Detail label="Attempts" value={String(detail.attempts)} testid="email-log-detail-attempts" />
              <Detail label="Provider ID" value={detail.provider_id} mono />
              <Detail label="Dedupe key" value={detail.dedupe_key} mono />
              <Detail label="Next retry" value={detail.next_attempt_at ? new Date(detail.next_attempt_at).toLocaleString() : null} />
              <div className="col-span-2">
                <Detail label="Last error" value={detail.last_error} testid="email-log-detail-error" />
              </div>
              <div className="col-span-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">Payload</p>
                <pre className="mt-1 max-h-48 overflow-auto rounded-md border border-border bg-sand/40 p-3 font-mono text-xs text-charcoal" data-testid="email-log-detail-payload">
                  {JSON.stringify(detail.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
